import type { CSSProperties } from 'react'
import { intlLocale } from '@/i18n'
import type { Lesson, Period } from './types'

/** Localised weekday name for a 0 = Sunday … 6 = Saturday index, capitalised for headers. */
export function weekdayName(dayOfWeek: number, style: 'long' | 'short' = 'long'): string {
  // 7 January 2024 was a Sunday.
  const name = new Intl.DateTimeFormat(intlLocale(), { weekday: style }).format(
    new Date(2024, 0, 7 + dayOfWeek),
  )
  return name.charAt(0).toLocaleUpperCase(intlLocale()) + name.slice(1)
}

export function toMinutes(hhmm: string): number {
  const [h = '0', m = '0'] = hhmm.split(':')
  return Number(h) * 60 + Number(m)
}

/** "07:30" → "07:30" (fr) / "7:30 AM" (en), following the UI language. */
export function formatTime(hhmm: string): string {
  const minutes = toMinutes(hhmm)
  return new Intl.DateTimeFormat(intlLocale(), { hour: 'numeric', minute: '2-digit' }).format(
    new Date(1970, 0, 1, Math.floor(minutes / 60), minutes % 60),
  )
}

export function timeRange(period: Pick<Period, 'startTime' | 'endTime'>): string {
  return `${formatTime(period.startTime)} – ${formatTime(period.endTime)}`
}

export function lessonKey(dayOfWeek: number, periodId: number): string {
  return `${dayOfWeek}-${periodId}`
}

export function indexLessons(lessons: Lesson[]): Map<string, Lesson> {
  return new Map(lessons.map((l) => [lessonKey(l.dayOfWeek, l.periodId), l]))
}

export interface NowNext {
  /** Today's day of week when the school is open today, otherwise null. */
  today: number | null
  nowPeriodId: number | null
  nextPeriodId: number | null
}

/** Which period is running now and which lesson period comes next today (local time). */
export function nowAndNext(days: number[], periods: Period[], now: Date = new Date()): NowNext {
  const today = now.getDay()
  if (!days.includes(today)) return { today: null, nowPeriodId: null, nextPeriodId: null }
  const minutes = now.getHours() * 60 + now.getMinutes()
  const current = periods.find((p) => toMinutes(p.startTime) <= minutes && minutes < toMinutes(p.endTime))
  const next = periods.find((p) => !p.isBreak && toMinutes(p.startTime) > minutes)
  return { today, nowPeriodId: current?.id ?? null, nextPeriodId: next?.id ?? null }
}

/** The day to open first on phones: today when the school is open, else the first school day. */
export function defaultDay(days: number[], now: Date = new Date()): number | undefined {
  return days.includes(now.getDay()) ? now.getDay() : days[0]
}

const HEX = /^#[0-9a-f]{6}$/i

/**
 * Subject colours are data (validated hex from the API). The tint is mixed into the surface token
 * so it stays soft and readable in both light and dark themes; text keeps the ink token.
 */
export function subjectStyle(color: string | null): CSSProperties | undefined {
  if (!color || !HEX.test(color)) return undefined
  return {
    borderLeftColor: color,
    backgroundColor: `color-mix(in srgb, ${color} 13%, var(--surface))`,
  }
}

export function subjectDotStyle(color: string | null): CSSProperties | undefined {
  return color && HEX.test(color) ? { backgroundColor: color } : undefined
}
